import React, { useState } from 'react';
import * as TabilityTypes from 'types';

import { useQuery, useMutation, useQueryCache } from 'react-query';
import queryKeys from 'config/queryKeys';

// API
import * as remoteApi from 'api/remote';

import FormField from 'components/FormField';
import KoalaButton from 'koala/components/Button';
import KoalaLoader from 'koala/components/Loader';
import { translate } from 'utils/customTermUtils';
import { CustomTermKey } from 'utils/customTermUtils';
import { shallowEqual, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
interface CurrentValueProps {
  outcome: TabilityTypes.Outcome;
}

function CurrentValue(props: CurrentValueProps) {
  const { outcome } = props;

  // Query keys and query params
  const queryKey = [
    queryKeys.currentOutcome,
    outcome.id,
    `data_source:dependencies:${outcome.data_source_type}`,
    'current',
  ];
  const staleTime = 300;

  const { data, isLoading } = useQuery(queryKey, remoteApi.fetchOutcomeDataSourceCurrentValue, {
    staleTime,
  });

  if (isLoading) {
    return (
      <FormField>
        <KoalaLoader />
      </FormField>
    );
  }

  const currentValue = data?.data.result;

  if (currentValue === null) {
    return <FormField>Sorry, an error occurred while fetching the current value.</FormField>;
  }

  const dataTypeToLabel: any = {
    percentage: '<number>%',
    count: '<number> completed',
  };

  const label = dataTypeToLabel[outcome.data_source_type || ''];
  const labelWithNumber = label.replace('<number>', currentValue.toLocaleString());

  return (
    <FormField>
      <label>Preview value</label>
      <p>{labelWithNumber}</p>
    </FormField>
  );
}

interface Props {
  outcome: TabilityTypes.Outcome;
}

function InitiativesSource(props: Props) {
  const { outcome } = props;
  const currentWorkspace = useSelector((state: any) => state.session.currentWorkspace, shallowEqual);
  const { t } = useTranslation();
  const queryCache = useQueryCache();
  const [data_source_type, setDataSourceType] = useState(outcome.data_source_type || '');

  // Functions to update the outcome
  const [updateOutcomeDataSourceMutation, { isLoading: isUpdating }]: [any, any] = useMutation(
    remoteApi.updateOutcomeDataSource,
    {
      onSuccess: (response) => {
        queryCache.invalidateQueries(queryKeys.currentOutcome);
      },
    },
  );

  const handleUpdate = (e: any) => {
    e.preventDefault();
    let body = {
      data_source_origin: 'initiatives',
      data_source_type: data_source_type,
    };

    const mutationParams = {
      outcomeId: outcome.nano_slug,
      body,
    };
    updateOutcomeDataSourceMutation(mutationParams);
  };
  const availableDataSourceTypes = ['percentage', 'count'];

  return (
    <>
      <FormField>
        <p>
          {t('panels.editOutcome.initiativeInfo', {
            outcome: translate(currentWorkspace, CustomTermKey.OUTCOME, 1).toLowerCase(),
            initiatives: translate(currentWorkspace, CustomTermKey.INITIATIVE, 2).toLowerCase(),
          })}
        </p>
      </FormField>
      <FormField>
        <label>Select aggregation type</label>
        <select value={data_source_type} onChange={(e) => setDataSourceType(e.currentTarget.value)}>
          <option value="">Select an option...</option>
          <option value="percentage">Percentage</option>
          <option value="count">Count</option>
        </select>
      </FormField>

      {outcome.data_source_type !== data_source_type && (
        <FormField>
          <KoalaButton onClick={handleUpdate} loading={isUpdating} disabled={isUpdating} appearance="subtle">
            Save & preview
          </KoalaButton>
        </FormField>
      )}
      {outcome.data_source_origin === 'initiatives' &&
        outcome.data_source_type &&
        outcome.data_source_type === data_source_type && (
          <>{availableDataSourceTypes.includes(outcome.data_source_type) && <CurrentValue outcome={outcome} />}</>
        )}
    </>
  );
}

export default React.memo(InitiativesSource);
